import React, {
  useContext, useEffect, useMemo, useState, memo
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Grid,
  Button,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, List, ListItemButton, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  ContentCopy,
  Delete,
  Add,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';

import DashboardTopMenu from './DashboardTopMenu';
import { ThemeContext, ConstantsContext } from '../index';

/* ===================== Variables & previews ===================== */
const VARS = {
  portfolio: [
    { key: 'all_coins_with_buy_limit_and_buy_stop_limit_open_orders_only', label: 'all_coins_with_buy_limit_and_buy_stop_limit_open_orders_only' },
    { key: 'all_coins_that_has_positive_balance', label: 'all_coins_that_has_positive_balance' },
    { key: 'available_coins_list_to_trade', label: 'available_coins_list_to_trade' },
    { key: 'available_coins_to_buy', label: 'available_coins_to_buy' },
  ],
  coin: [
    { key: 'selected_coin', label: 'selected_coin' }, // placeholder
  ],
  news: [
    { key: 'latest_news', label: 'latest_news' }, // placeholder
  ],
};

const variablePreviewText = (key) => {
  switch (key) {
    case 'all_coins_with_buy_limit_and_buy_stop_limit_open_orders_only':
      return `MANA 308276.00000000 (units) current price @ $0.31920000 and balance in usd 98401.69920000 | NEAR 36496.50000000 (units) current price @ $2.68600000 and balance in usd 98029.59900000`;
    case 'all_coins_that_has_positive_balance':
      return `MATIC 365490.00000000 (units) current price @ $0.26730000 and balance in usd 97695.47700000 | DOT 23991.90000000 (units) current price @ $4.15900000 and balance in usd 99782.31210000 | MANA 308276.00000000 (units) current price @ $0.31920000 and balance in usd 98401.69920000 | NEAR 36496.50000000 (units) current price @ $2.68600000 and balance in usd 98029.59900000 | SOL 448.11000000 (units) current price @ $226.46000000 and balance in usd 101478.99060000 | ADA 113938.00000000 (units) current price @ $0.87660000 and balance in usd 99878.05080000 | ALGO 207568.00000000 (units) current price @ $0.23910000 and balance in usd 49629.50880000 | AVAX 3507.71000000 (units) current price @ $28.87000000 and balance in usd 101267.58770000 | XLM 262775.00000000 (units) current price @ $0.38559800 and balance in usd 101325.51445000 | LTC 865.08000000 (units) current price @ $113.80000000 and balance in usd 98446.10400000 | BONK 2152440000.00000000 (units) current price @ $0.00002341 and balance in usd 50388.62040000`;
    case 'available_coins_list_to_trade':
      return `ADA current price @ $0.87660000, ALGO current price @ $0.23910000, AVAX current price @ $28.87000000, BONK current price @ $0.00002341, BTC current price @ $114020.00000000, CRO current price @ $0.25330000, DOGE current price @ $0.24622000, DOT current price @ $4.15900000, ETC current price @ $21.08000000, ETH current price @ $4402.66000000, JASMY current price @ $0.01418000, LINK current price @ $23.49100000, LTC current price @ $113.80000000, MANA current price @ $0.31920000, MATIC current price @ $0.26730000, NEAR current price @ $2.68600000, SHIB current price @ $0.00001296, SOL current price @ $226.46000000, SUI current price @ $3.58100000, XLM current price @ $0.38559800, XRP current price @ $2.99090000`;
    case 'available_coins_to_buy':
      return `BTC current price @ $114020.00000000, CRO current price @ $0.25330000, DOGE current price @ $0.24622000, ETC current price @ $21.08000000, ETH current price @ $4402.66000000, JASMY current price @ $0.01418000, LINK current price @ $23.49100000, SHIB current price @ $0.00001296, SUI current price @ $3.58100000, XRP current price @ $2.99090000`;
    default:
      return 'No preview available.';
  }
};
/* ================================================================ */

export default function RouteAIView(props) {
  const viewPaneRef = React.useRef(null);
  const editPaneRef = React.useRef(null);
  const isWide = useMediaQuery('(min-width: 1100px)');
  const { id } = useParams(); // /AI/:id
  const history = useHistory();
  const [theme] = useContext(ThemeContext);
  const [staticText] = useContext(ConstantsContext);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [prompt, setPrompt] = useState(null);

  // editable working copy of json_info (array of sections)
  const [sections, setSections] = useState([]);
  const [originalSections, setOriginalSections] = useState([]);
  const [saving, setSaving] = useState(false);

  const apiPath = staticText.path_darts;

  const clone = (obj) => JSON.parse(JSON.stringify(obj));

  const [selectedIdx, setSelectedIdx] = useState(null);

  const [jumpIdx, setJumpIdx] = useState('');

  // ===== Add Variable Modal state =====
  const [varModal, setVarModal] = useState({
    open: false,
    sIdx: null,
    category: 'portfolio',
    variable: 'all_coins_with_buy_limit_and_buy_stop_limit_open_orders_only',
  });

  // --- base64 <-> utf8 helpers ---
const b64ToUtf8 = (b64) => {
  if (!b64) return '';
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array([...bin].map(c => c.charCodeAt(0)));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
};

const utf8ToB64 = (str) => {
  try {
    const bytes = new TextEncoder().encode(str);
    let bin = '';
    bytes.forEach(b => (bin += String.fromCharCode(b)));
    return btoa(bin);
  } catch {
    return '';
  }
};

// local editable prompt text
const [promptText, setPromptText] = useState('');

// Editable fields for this prompt
const [nameText, setNameText] = React.useState('');
const [descText, setDescText] = React.useState('');



  const setSelectedIdxFnc = (idx, origin /* 'view' | 'edit' */) => {
    // capture the scroll position of the pane where the click happened
    const originEl = origin === 'view' ? viewPaneRef.current : editPaneRef.current;
    const savedTop = originEl ? originEl.scrollTop : null;

    setSelectedIdx(idx);
    setJumpIdx(String(idx));

    // scroll the opposite pane to the right section
    const targetId = origin === 'view' ? `edit_${idx}` : `view_${idx}`;
    const target = document.getElementById(targetId);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // restore the origin pane scroll *after* the render completes
    requestAnimationFrame(() => {
      if (originEl && savedTop != null) originEl.scrollTop = savedTop;
    });
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleJumpSelect = (e) => {
    const idx = Number(e.target.value);
    if (!Number.isNaN(idx)) {
      setJumpIdx(String(idx));
      setSelectedIdx(idx);
      scrollToId(`view_${idx}`);
      scrollToId(`edit_${idx}`);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = () => {
    const data = [
      { key: 'action', value: 'get_prompt' },
      { key: 'loggined_token', value: props.userDetails?.loggined_token },
      { key: 'prompt_id', value: String(id) },
    ];

    setLoading(true);
    setErrorMsg('');
    props._apiConnect(apiPath, data, (res) => {
      setLoading(false);
      if (String(res?.status_code) === '2' && res?.prompt) {
        const p = res.prompt;
        setPrompt(p);

        setNameText(p?.name || '');
        setDescText(p?.description || '');

        // NEW: decode base64 prompt to editable text
        const decodedPrompt = b64ToUtf8(p?.prompt || '');
        setPromptText(decodedPrompt);

        // parse json_info to array
        let parsed = [];
        try {
          parsed =
            typeof p.json_info === 'string'
              ? JSON.parse(p.json_info || '[]')
              : Array.isArray(p.json_info)
              ? p.json_info
              : [];
        } catch {
          parsed = [];
        }
        // normalize
        const norm = (parsed || []).map((s) => ({
          title: s?.title ?? '',
          points: Array.isArray(s?.points) ? s.points.slice() : [],
          ...s,
        }));
        setSections(norm);
        setOriginalSections(JSON.parse(JSON.stringify(norm)));
      } else {
        setErrorMsg(res?.msg || 'Failed to load prompt');
        setPrompt(null);
        setSections([]);
        setOriginalSections([]);
        setPromptText('');
      }
    });
  };


  // ---------- helpers ----------
  const rgba = (hex, a) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    if (!m) return hex || 'rgba(0,0,0,0.25)';
    const r = parseInt(m[1], 16),
      g = parseInt(m[2], 16),
      b = parseInt(m[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const cardStyle = {
    background: 'transparent',
    border: `1px solid ${rgba(theme.foreground, 0.25)}`,
    padding: 12,
    color: theme.foreground,
  };

  // ---------- View tab renderer ----------
  const ViewBlock = memo(function ViewBlock({ s, idx }) {
    const isSelected = selectedIdx === idx;
    return (
      <div
        id={`view_${idx}`}
        style={{
          marginBottom: 16,
          border: isSelected ? `1px solid ${theme.btn_color}` : `1px solid transparent`,
          borderRadius: 6,
          padding: isSelected ? 8 : 0,
        }}
      >
        {(s.title || s.title === '') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Typography variant="subtitle1" style={{ color: theme.foreground, fontWeight: 700, flex: 1 }}>
              {s.title}
            </Typography>
            <Button
              size="small"
              variant={isSelected ? 'contained' : 'outlined'}
              onClick={() => setSelectedIdxFnc(idx, 'view')}
              sx={{ color: isSelected ? theme.background : theme.btn_color, borderColor: theme.btn_color, minWidth: 88 }}
            >
              {`Section #${idx + 1}`}
            </Button>
          </div>
        )}

        {Array.isArray(s.points) && s.points.length > 0 && (
          <ul style={{ margin: '6px 0 0 18px' }}>
            {s.points.map((pt, i) => {
              // NEW: Variable point preview
              if (pt && typeof pt === 'object' && pt.variable) {
                return (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Variable: {pt.variable}</div>
                    <div>{variablePreviewText(pt.variable)}</div>
                  </li>
                );
              }

              if (typeof pt === 'string') return <li key={i} style={{ marginBottom: 4 }}>{pt}</li>;
              if (pt?.code) {
                return (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        border: `1px solid ${rgba(theme.foreground, 0.2)}`,
                        padding: 8,
                        borderRadius: 6,
                        background: 'transparent',
                      }}
                    >
                      {pt.code}
                    </pre>
                  </li>
                );
              }
              if (pt?.text) {
                return (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {pt.text}
                    {Array.isArray(pt.subpoints) && pt.subpoints.length > 0 && (
                      <ul style={{ marginTop: 6 }}>
                        {pt.subpoints.map((sp, j) => (
                          <li key={j}>{sp}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }
              if (pt?.title_only) {
                return <li key={i} style={{ marginBottom: 4, listStyle: 'none', fontWeight: 600 }} />;
              }
              return (
                <li key={i} style={{ marginBottom: 4 }}>
                  <code>{JSON.stringify(pt)}</code>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  });

  // ---------- Edit logic ----------
  const addSection = () => addSectionAt(0);

  const addSectionAt = (index) => {
    const draft = {
      title: 'New Rule:',
      points: ['Describe Rule sub point 1', 'Describe Rule sub point 2'],
    };
    setSections(prev => {
      const copy = [...prev];
      const i = Math.max(0, Math.min(index, copy.length));
      copy.splice(i, 0, draft);
      return copy;
    });

    const to = Math.max(0, Math.min(index, sections.length));
    setSelectedIdx(to);
    setJumpIdx(String(to));
    requestAnimationFrame(() => {
      document.getElementById(`edit_${to}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      document.getElementById(`view_${to}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const InsertBar = ({ index }) => (
    <div style={{ margin: '8px 0', display: 'flex', justifyContent: 'center' }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<Add />}
        onClick={() => addSectionAt(index)}
        sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
      >
        Add Section
      </Button>
    </div>
  );

  const duplicateSection = (index) => {
    setSections((arr) => {
      const copy = JSON.parse(JSON.stringify(arr[index]));
      return [...arr.slice(0, index + 1), copy, ...arr.slice(index + 1)];
    });
  };

  const moveSection = (index, dir) => {
    setSections((arr) => {
      const newIdx = index + dir;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      const cloneArr = arr.slice();
      const [item] = cloneArr.splice(index, 1);
      cloneArr.splice(newIdx, 0, item);
      return cloneArr;
    });
  };

  const deleteSection = (index) => {
    setSections((arr) => arr.filter((_, i) => i !== index));
  };

  const updateSectionTitle = (index, value) => {
    setSections((arr) => {
      const cloneArr = arr.slice();
      cloneArr[index] = { ...cloneArr[index], title: value };
      return cloneArr;
    });
  };

  // points helpers
  const addPoint = (sIdx) => {
    setSections((arr) => {
      const cloneArr = arr.slice();
      const sec = { ...cloneArr[sIdx] };
      const pts = Array.isArray(sec.points) ? sec.points.slice() : [];
      pts.push('New point');
      sec.points = pts;
      cloneArr[sIdx] = sec;
      return cloneArr;
    });
  };

  const updatePoint = (sIdx, pIdx, value) => {
    setSections((arr) => {
      const cloneArr = arr.slice();
      const sec = { ...cloneArr[sIdx] };
      const pts = sec.points.slice();
      pts[pIdx] = value;
      sec.points = pts;
      cloneArr[sIdx] = sec;
      return cloneArr;
    });
  };

  const movePoint = (sIdx, pIdx, dir) => {
    setSections((arr) => {
      const cloneArr = arr.slice();
      const sec = { ...cloneArr[sIdx] };
      const pts = sec.points.slice();
      const newIdx = pIdx + dir;
      if (newIdx < 0 || newIdx >= pts.length) return arr;
      const [item] = pts.splice(pIdx, 1);
      pts.splice(newIdx, 0, item);
      sec.points = pts;
      cloneArr[sIdx] = sec;
      return cloneArr;
    });
  };

  const deletePoint = (sIdx, pIdx) => {
    setSections((arr) => {
      const cloneArr = arr.slice();
      const sec = { ...cloneArr[sIdx] };
      const pts = sec.points.slice();
      pts.splice(pIdx, 1);
      sec.points = pts;
      cloneArr[sIdx] = sec;
      return cloneArr;
    });
  };

  const duplicatePoint = (sIdx, pIdx) => {
    setSections((arr) => {
      const cloneArr = arr.slice();
      const sec = { ...cloneArr[sIdx] };
      const pts = sec.points.slice();
      const item = JSON.parse(JSON.stringify(pts[pIdx]));
      pts.splice(pIdx + 1, 0, item);
      sec.points = pts;
      cloneArr[sIdx] = sec;
      return cloneArr;
    });
  };

  const resetEdits = () => {
    setSections(JSON.parse(JSON.stringify(originalSections)));
  };

  // object point helpers
  const updateObjectPointText = (sIdx, pIdx, newText) => {
    setSections(prev => {
      const copy = clone(prev);
      if (!copy[sIdx]?.points?.[pIdx]) return prev;
      copy[sIdx].points[pIdx] = {
        ...(typeof copy[sIdx].points[pIdx] === 'object' ? copy[sIdx].points[pIdx] : { text: '', subpoints: [] }),
        text: newText,
      };
      return copy;
    });
  };

  const addSubpoint = (sIdx, pIdx) => {
    setSections(prev => {
      const copy = clone(prev);
      const pt = copy[sIdx]?.points?.[pIdx];
      if (!pt || typeof pt !== 'object') return prev;
      pt.subpoints = Array.isArray(pt.subpoints) ? pt.subpoints : [];
      pt.subpoints.push('New subpoint');
      return copy;
    });
  };

  const updateSubpoint = (sIdx, pIdx, spIdx, value) => {
    setSections(prev => {
      const copy = clone(prev);
      const sub = copy[sIdx]?.points?.[pIdx]?.subpoints;
      if (!Array.isArray(sub)) return prev;
      sub[spIdx] = value;
      return copy;
    });
  };

  const moveSubpoint = (sIdx, pIdx, spIdx, dir) => {
    setSections(prev => {
      const copy = clone(prev);
      const sub = copy[sIdx]?.points?.[pIdx]?.subpoints;
      if (!Array.isArray(sub)) return prev;
      const to = spIdx + dir;
      if (to < 0 || to >= sub.length) return prev;
      [sub[spIdx], sub[to]] = [sub[to], sub[spIdx]];
      return copy;
    });
  };

  const deleteSubpoint = (sIdx, pIdx, spIdx) => {
    setSections(prev => {
      const copy = clone(prev);
      const sub = copy[sIdx]?.points?.[pIdx]?.subpoints;
      if (!Array.isArray(sub)) return prev;
      sub.splice(spIdx, 1);
      return copy;
    });
  };

  const duplicateObjectPoint = (sIdx, pIdx) => {
    setSections(prev => {
      const copy = clone(prev);
      const pts = copy[sIdx]?.points;
      if (!Array.isArray(pts)) return prev;
      pts.splice(pIdx + 1, 0, clone(pts[pIdx]));
      return copy;
    });
  };

  const addObjectPoint = (sIdx) => {
    setSections(prev => {
      const copy = clone(prev);
      const pts = copy[sIdx]?.points;
      if (!Array.isArray(pts)) copy[sIdx].points = [];
      copy[sIdx].points.push({ text: 'New Rule:', subpoints: ['Describe Rule sub point 1', 'Describe Rule sub point 2'] });
      return copy;
    });
  };

  // ===== Add Variable control =====
  const openVarModal = (sIdx) => {
    setVarModal({
      open: true,
      sIdx,
      category: 'portfolio',
      variable: 'all_coins_with_buy_limit_and_buy_stop_limit_open_orders_only',
    });
  };
  const closeVarModal = () => setVarModal(v => ({ ...v, open: false }));

  const addVariablePointToSection = () => {
    const { sIdx, category, variable } = varModal;
    if (sIdx == null) return;

    setSections(prev => {
      const copy = clone(prev);
      if (!copy[sIdx]) return prev;
      const pts = Array.isArray(copy[sIdx].points) ? copy[sIdx].points.slice() : [];
      pts.push({ variable, category });
      copy[sIdx] = { ...copy[sIdx], points: pts };
      return copy;
    });

    closeVarModal();
  };

  const saveEdits = async () => {
    if (!prompt) return;
    setSaving(true);

    const fd = new FormData();
    fd.append('action', 'update_prompt');
    fd.append('loggined_token', props.userDetails?.loggined_token || '');
    fd.append('prompt_id', String(prompt.id));
    fd.append('name', prompt.name || '');
    fd.append('description', prompt.description || '');
    fd.append('json_info', JSON.stringify(sections));

    try {
      const resp = await fetch(staticText.path_darts, {
        method: 'POST',
        body: fd,
      });
      const res = await resp.json();
      if (String(res?.status_code) === '2') {
        load(); // refresh
      } else {
        alert(res?.msg || 'Failed to update prompt');
      }
    } catch (e) {
      alert('Network error while updating prompt');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const updatePromptFreeText = () => {
    if (!promptText.trim()) {
      alert('Prompt text is empty.');
      return;
    }
    if (!nameText.trim()) {
      alert('Please enter a name.');
      return;
    }

    const data = [
      { key: 'action', value: 'update_prompt_free_text_only' },
      { key: 'loggined_token', value: props.userDetails?.loggined_token },
      { key: 'prompt_id', value: String(prompt?.id || id) },

      // new fields
      { key: 'name', value: nameText.trim() },
      { key: 'description', value: descText.trim() },

      // prompt text (keep URL-encoded for backend)
      { key: 'prompt', value: encodeURIComponent(promptText) },
    ];

    setLoading(true);
    props._apiConnect(apiPath, data, (res) => {
      setLoading(false);
      if (String(res?.status_code) === '2') {
        console.log('✅ Prompt updated successfully:', res);
        // optional: reload to reflect server copy
        // load();
      } else {
        console.error('❌ Prompt update failed:', res);
        alert(res?.msg || 'Failed to update prompt');
      }
    });
  };



  const sectionOptions = (sections || []).map((s, i) => {
    const label = (s?.title?.trim?.() ? s.title.trim() : `Section #${i + 1}`);
    return { value: i, label: `${i + 1}. ${label}` };
  });

  const prettyJson = useMemo(() => {
    try {
      return JSON.stringify(sections, null, 2);
    } catch {
      return '';
    }
  }, [sections]);

  return (
    <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
      <DashboardTopMenu _apiLogOff={props._apiLogOff} userDetails={props.userDetails} />

      {/* Header row */}
      <Grid item xs={12} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button
          variant="outlined"
          onClick={() => history.push('/AI')}
          sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
        >
          Back to Prompts
        </Button>
        <Typography variant="h5" style={{ color: theme.foreground }}>
          Prompt #{id}
        </Typography>
      </Grid>

      {/* Meta */}
      {!loading && prompt && (
        <Grid item xs={12}>
          <Paper elevation={0} style={{ background: 'transparent', color: theme.foreground }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div><b>Owner:</b> {prompt.owner_username || '—'} {prompt.owner_user_id ? `#${prompt.owner_user_id}` : ''}</div>
              <div><b>Linked users:</b> {prompt.linked_users_count ?? (prompt.linked_users?.length || 0)}</div>
              <div><b>Created:</b> {prompt.created_at || '—'}</div>
              <div><b>Updated:</b> {prompt.updated_at || '—'}</div>
            </div>
          </Paper>
        </Grid>
      )}

      {/* Content */}
      <Grid item xs={12}>
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: theme.foreground }}>
            <CircularProgress size={18} sx={{ color: theme.btn_color }} />
            Loading…
          </div>
        )}

        {!loading && errorMsg && <div style={{ color: '#d32f2f' }}>{errorMsg}</div>}

        {!loading && prompt && (
          <>
            <Paper elevation={0} style={cardStyle}>
              {/* Editable Prompt Body */}
              <div style={{ marginTop: 16 }}>
                {/* Name */}
                <input
                  type="text"
                  placeholder="Prompt name"
                  value={nameText}
                  onChange={(e) => setNameText(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.btn_color_border}`,
                    borderRadius: 6,
                    background: theme.background,
                    color: theme.foreground,
                  }}
                />
                <div style={{paddingBottom:10}}></div>

                {/* Description */}
                <textarea
                  placeholder="Prompt description"
                  value={descText}
                  onChange={(e) => setDescText(e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.btn_color_border}`,
                    borderRadius: 6,
                    background: theme.background,
                    color: theme.foreground,
                    resize: 'vertical',
                  }}
                />
                <div style={{paddingBottom:10}}></div>
                <label
                  style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}
                >
                  Prompt
                </label>
                <textarea
                  rows={10}
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  placeholder="Write or edit your prompt here…"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #4b5563',
                    borderRadius: 8,
                    background: '#0b1220',
                    color: '#e5e7eb',
                    resize: 'vertical',
                  }}
                />
                <div style={{ marginTop: 12 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    style={{ marginTop: 16 }}
                    onClick={updatePromptFreeText}
                  >
                    💾 Update Prompt Free Text
                  </Button>

                </div>
              </div>
              <div style={{paddingBottom:20}}></div>

              {/* Top controls */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                {/* LEFT: title */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" style={{ color: theme.foreground }}>
                    Prompt
                  </Typography>
                </div>

                {/* CENTER: Reset + Save */}
                <div style={{ flexShrink: 0, display: 'flex', gap: 8 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ResetIcon />}
                    onClick={resetEdits}
                    sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
                  >
                    Reset
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveEdits}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                </div>

                {/* RIGHT: fast jump select */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                  <select
                    value={jumpIdx}
                    onChange={handleJumpSelect}
                    style={{
                      maxWidth: 360,
                      padding: '6px 8px',
                      border: `1px solid ${theme.btn_color_border}`,
                      borderRadius: 6,
                      background: theme.background,
                      color: theme.foreground,
                    }}
                  >
                    <option value="" disabled>Jump to section…</option>
                    {sectionOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>


              {/* Two panes */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <div
                    ref={viewPaneRef}
                    style={{
                      border: `1px solid ${rgba(theme.foreground, 0.2)}`,
                      borderRadius: 8,
                      padding: 12,
                      background: 'transparent',
                      color: theme.foreground,
                      height: 'calc(100vh - 260px)',
                      overflowY: 'auto'
                    }}
                  >
                    <Typography variant="subtitle1" style={{ marginBottom: 8, color: theme.foreground, fontWeight: 700 }}>
                      View
                    </Typography>
                    <div>
                      {sections.length === 0 && <div>Empty prompt.</div>}
                      {sections.map((s, idx) => (<ViewBlock key={idx} s={s} idx={idx} />))}
                      <details style={{ marginTop: 12 }}>
                        <summary style={{ cursor: 'pointer' }}>Raw JSON</summary>
                        <pre style={{
                          margin: 0, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre',
                          fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace',
                          fontSize: 13,
                        }}>{prettyJson}</pre>
                      </details>
                    </div>
                  </div>
                </Grid>

                <Grid item xs={12} md={6}>
                  <div
                    ref={editPaneRef}
                    style={{
                      border: `1px solid ${rgba(theme.foreground, 0.2)}`,
                      borderRadius: 8,
                      padding: 12,
                      background: 'transparent',
                      color: theme.foreground,
                      height: 'calc(100vh - 260px)',
                      overflowY: 'auto'
                    }}
                  >
                    <Typography variant="subtitle1" style={{ marginBottom: 8, color: theme.foreground, fontWeight: 700 }}>
                      Edit
                    </Typography>

                    <div>
                      <InsertBar index={0} />

                      {sections.length === 0 && <div style={{ marginBottom: 12 }}>No sections yet.</div>}

                      {sections.map((s, sIdx) => (
                        <React.Fragment key={sIdx}>
                          <Paper
                            elevation={0}
                            style={{
                              border: selectedIdx === sIdx
                                ? `2px solid ${theme.btn_color}`
                                : `1px dashed ${rgba(theme.foreground, 0.2)}`,
                              padding: 12,
                              marginBottom: 12,
                              background: 'transparent',
                              color: theme.foreground,
                              borderRadius: 8,
                            }}
                          >
                            {/* Section toolbar */}
                            <div id={`edit_${sIdx}`} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                              <Button
                                size="small"
                                variant={selectedIdx === sIdx ? 'contained' : 'outlined'}
                                onClick={() => setSelectedIdxFnc(sIdx, 'edit')}
                                sx={{ color: selectedIdx === sIdx ? theme.background : theme.btn_color, borderColor: theme.btn_color, minWidth: 88 }}
                              >
                                {`Section #${sIdx + 1}`}
                              </Button>
                              <div style={{ flex: 1 }} />
                              <Tooltip title="Move up">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveSection(sIdx, -1)}
                                    disabled={sIdx === 0}
                                    sx={{ color: theme.foreground }}
                                  >
                                    <ArrowUpward fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Move down">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveSection(sIdx, +1)}
                                    disabled={sIdx === sections.length - 1}
                                    sx={{ color: theme.foreground }}
                                  >
                                    <ArrowDownward fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Duplicate section">
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateSection(sIdx)}
                                  sx={{ color: theme.foreground }}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete section">
                                <IconButton
                                  size="small"
                                  onClick={() => deleteSection(sIdx)}
                                  sx={{ color: theme.foreground }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>

                            {/* Title input */}
                            <input
                              type="text"
                              value={s.title || ''}
                              onChange={(e) => updateSectionTitle(sIdx, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                marginBottom: 10,
                                border: `1px solid ${theme.btn_color_border}`,
                                borderRadius: 4,
                                background: theme.background,
                                color: theme.foreground,
                              }}
                            />

                            {/* Points */}
                            <Typography variant="subtitle2" style={{ color: theme.foreground, marginBottom: 6 }}>
                              Points
                            </Typography>

                            {Array.isArray(s.points) && s.points.length > 0 ? (
                              s.points.map((pt, pIdx) => {
                                const isString = typeof pt === 'string';
                                const isObjWithText = pt && typeof pt === 'object' && 'text' in pt;

                                if (isString) {
                                  return (
                                    <div
                                      key={pIdx}
                                      style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}
                                    >
                                      <input
                                        type="text"
                                        value={pt}
                                        onChange={(e) => updatePoint(sIdx, pIdx, e.target.value)}
                                        style={{
                                          flex: 1,
                                          padding: '6px 8px',
                                          border: `1px solid ${theme.btn_color_border}`,
                                          borderRadius: 4,
                                          background: theme.background,
                                          color: theme.foreground,
                                        }}
                                      />
                                      <Tooltip title="Move up">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => movePoint(sIdx, pIdx, -1)}
                                            disabled={pIdx === 0}
                                            sx={{ color: theme.foreground }}
                                          >
                                            <ArrowUpward fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Move down">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => movePoint(sIdx, pIdx, +1)}
                                            disabled={pIdx === s.points.length - 1}
                                            sx={{ color: theme.foreground }}
                                          >
                                            <ArrowDownward fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Duplicate point">
                                        <IconButton
                                          size="small"
                                          onClick={() => duplicatePoint(sIdx, pIdx)}
                                          sx={{ color: theme.foreground }}
                                        >
                                          <ContentCopy fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete point">
                                        <IconButton
                                          size="small"
                                          onClick={() => deletePoint(sIdx, pIdx)}
                                          sx={{ color: theme.foreground }}
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                  );
                                }

                                if (isObjWithText) {
                                  const subpoints = Array.isArray(pt.subpoints) ? pt.subpoints : [];
                                  return (
                                    <div
                                      key={pIdx}
                                      style={{
                                        border: `1px dashed ${rgba(theme.foreground, 0.25)}`,
                                        borderRadius: 6,
                                        padding: 10,
                                        marginBottom: 10,
                                      }}
                                    >
                                      {/* point title */}
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <input
                                          type="text"
                                          value={pt.text || ''}
                                          onChange={(e) => updateObjectPointText(sIdx, pIdx, e.target.value)}
                                          style={{
                                            flex: 1,
                                            padding: '6px 8px',
                                            border: `1px solid ${theme.btn_color_border}`,
                                            borderRadius: 4,
                                            background: theme.background,
                                            color: theme.foreground,
                                            fontWeight: 600,
                                          }}
                                          placeholder="Point title"
                                        />

                                        <Tooltip title="Move up">
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => movePoint(sIdx, pIdx, -1)}
                                              disabled={pIdx === 0}
                                              sx={{ color: theme.foreground }}
                                            >
                                              <ArrowUpward fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Move down">
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => movePoint(sIdx, pIdx, +1)}
                                              disabled={pIdx === s.points.length - 1}
                                              sx={{ color: theme.foreground }}
                                            >
                                              <ArrowDownward fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Duplicate (title + subpoints)">
                                          <IconButton
                                            size="small"
                                            onClick={() => duplicateObjectPoint(sIdx, pIdx)}
                                            sx={{ color: theme.foreground }}
                                          >
                                            <ContentCopy fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete point">
                                          <IconButton
                                            size="small"
                                            onClick={() => deletePoint(sIdx, pIdx)}
                                            sx={{ color: theme.foreground }}
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </div>

                                      {/* subpoints editor */}
                                      <div style={{ marginTop: 8, marginLeft: 8 }}>
                                        {subpoints.length > 0 ? (
                                          subpoints.map((sp, spIdx) => (
                                            <div
                                              key={spIdx}
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                marginBottom: 6,
                                              }}
                                            >
                                              <input
                                                type="text"
                                                value={sp}
                                                onChange={(e) => updateSubpoint(sIdx, pIdx, spIdx, e.target.value)}
                                                style={{
                                                  flex: 1,
                                                  padding: '6px 8px',
                                                  border: `1px solid ${theme.btn_color_border}`,
                                                  borderRadius: 4,
                                                  background: theme.background,
                                                  color: theme.foreground,
                                                }}
                                                placeholder={`Subpoint #${spIdx + 1}`}
                                              />
                                              <Tooltip title="Move up">
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => moveSubpoint(sIdx, pIdx, spIdx, -1)}
                                                    disabled={spIdx === 0}
                                                    sx={{ color: theme.foreground }}
                                                  >
                                                    <ArrowUpward fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                              <Tooltip title="Move down">
                                                <span>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => moveSubpoint(sIdx, pIdx, spIdx, +1)}
                                                    disabled={spIdx === subpoints.length - 1}
                                                    sx={{ color: theme.foreground }}
                                                  >
                                                    <ArrowDownward fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                              <Tooltip title="Delete subpoint">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => deleteSubpoint(sIdx, pIdx, spIdx)}
                                                  sx={{ color: theme.foreground }}
                                                >
                                                  <Delete fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            </div>
                                          ))
                                        ) : (
                                          <div style={{ marginBottom: 6, opacity: 0.8 }}>No subpoints.</div>
                                        )}

                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<Add />}
                                          onClick={() => addSubpoint(sIdx, pIdx)}
                                          sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
                                        >
                                          Add subpoint
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                }

                                // unknown object — protected
                                return (
                                  <div
                                    key={pIdx}
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}
                                  >
                                    <input
                                      type="text"
                                      value={JSON.stringify(pt)}
                                      disabled
                                      style={{
                                        flex: 1,
                                        padding: '6px 8px',
                                        border: `1px solid ${theme.btn_color_border}`,
                                        borderRadius: 4,
                                        background: theme.background,
                                        color: theme.foreground,
                                        opacity: 0.7,
                                      }}
                                      title="Non-text / protected point"
                                    />
                                    <Tooltip title="Move up">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => movePoint(sIdx, pIdx, -1)}
                                          disabled={pIdx === 0}
                                          sx={{ color: theme.foreground }}
                                        >
                                          <ArrowUpward fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Move down">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => movePoint(sIdx, pIdx, +1)}
                                          disabled={pIdx === s.points.length - 1}
                                          sx={{ color: theme.foreground }}
                                        >
                                          <ArrowDownward fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Duplicate">
                                      <IconButton
                                        size="small"
                                        onClick={() => duplicatePoint(sIdx, pIdx)}
                                        sx={{ color: theme.foreground }}
                                      >
                                        <ContentCopy fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={() => deletePoint(sIdx, pIdx)}
                                        sx={{ color: theme.foreground }}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                );
                              })
                            ) : (
                              <div style={{ marginBottom: 8 }}>No points.</div>
                            )}

                            {/* Add buttons row */}
                            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={() => addPoint(sIdx)}
                                sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
                              >
                                Add point
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={() => addObjectPoint(sIdx)}
                                sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
                              >
                                Add point with subpoints
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={() => openVarModal(sIdx)}
                                sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
                              >
                                Add variable
                              </Button>
                            </div>
                          </Paper>

                          {/* Insert bar after this section */}
                          <InsertBar index={sIdx + 1} />
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </Grid>

      {/* Add Variable modal */}
      <Dialog open={varModal.open} onClose={closeVarModal} fullWidth maxWidth="md">
        <DialogTitle>Add Variable</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: '60vh', display: 'grid', gridTemplateRows: '1fr 1fr', gap: 2 }}>
            {/* top half: 2 columns */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, minHeight: 0 }}>
              {/* left: categories */}
              <Box sx={{ border: `1px solid ${rgba(theme.foreground, 0.2)}`, borderRadius: 1, overflow: 'auto' }}>
                <List dense>
                  {['portfolio', 'coin', 'news'].map(cat => (
                    <ListItemButton
                      key={cat}
                      selected={varModal.category === cat}
                      onClick={() => {
                        const first = VARS[cat][0]?.key || '';
                        setVarModal(v => ({ ...v, category: cat, variable: first }));
                      }}
                    >
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </ListItemButton>
                  ))}
                </List>
              </Box>

              {/* right: variables */}
              <Box sx={{ border: `1px solid ${rgba(theme.foreground, 0.2)}`, borderRadius: 1, overflow: 'auto', p: 1 }}>
                <RadioGroup
                  value={varModal.variable}
                  onChange={(e) => setVarModal(v => ({ ...v, variable: e.target.value }))}
                >
                  {VARS[varModal.category].map(v => (
                    <FormControlLabel
                      key={v.key}
                      value={v.key}
                      control={<Radio />}
                      label={v.label}
                      sx={{ color: theme.foreground }}
                    />
                  ))}
                </RadioGroup>
              </Box>
            </Box>

            {/* bottom half: preview */}
            <Box sx={{
              border: `1px solid ${rgba(theme.foreground, 0.2)}`, borderRadius: 1,
              p: 2, overflow: 'auto', whiteSpace: 'pre-wrap', color: theme.foreground
            }}>
              {variablePreviewText(varModal.variable)}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVarModal}>Cancel</Button>
          <Button variant="contained" onClick={addVariablePointToSection}>Set variable</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
